import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { RequireAuth } from "./components/RequireAuth.js";
import { LeadNotificationsWatcher } from "./components/LeadNotificationsWatcher.js";

// Code-splitting por rota: o painel staff só carrega para usuário autenticado,
// mantendo o bundle das rotas públicas enxuto (ver CLAUDE.md §3).
const PublicBookingPage = lazy(() => import("./routes/public/BookingPage.js"));
const StaffLoginPage = lazy(() => import("./routes/staff/LoginPage.js"));
const StaffDashboardPage = lazy(() => import("./routes/staff/DashboardPage.js"));
const PatientsListPage = lazy(() => import("./routes/staff/patients/PatientsListPage.js"));
const NewPatientPage = lazy(() => import("./routes/staff/patients/NewPatientPage.js"));
const EditPatientPage = lazy(() => import("./routes/staff/patients/EditPatientPage.js"));
const PatientPhotosPage = lazy(() => import("./routes/staff/patients/PatientPhotosPage.js"));
const ClinicalRecordPage = lazy(() => import("./routes/staff/patients/ClinicalRecordPage.js"));
const AgendaPage = lazy(() => import("./routes/staff/agenda/AgendaPage.js"));
const ProfessionalsListPage = lazy(() => import("./routes/staff/professionals/ProfessionalsListPage.js"));
const EditProfessionalPage = lazy(() => import("./routes/staff/professionals/EditProfessionalPage.js"));
const UsersListPage = lazy(() => import("./routes/staff/users/UsersListPage.js"));
const NewUserPage = lazy(() => import("./routes/staff/users/NewUserPage.js"));
const EditUserPage = lazy(() => import("./routes/staff/users/EditUserPage.js"));
const ServicesListPage = lazy(() => import("./routes/staff/services/ServicesListPage.js"));
const NewServicePage = lazy(() => import("./routes/staff/services/NewServicePage.js"));
const ServiceCategoriesPage = lazy(() => import("./routes/staff/services/ServiceCategoriesPage.js"));
const InvoicesListPage = lazy(() => import("./routes/staff/invoices/InvoicesListPage.js"));
const NewInvoicePage = lazy(() => import("./routes/staff/invoices/NewInvoicePage.js"));
const PayablesListPage = lazy(() => import("./routes/staff/payables/PayablesListPage.js"));
const NewPayablePage = lazy(() => import("./routes/staff/payables/NewPayablePage.js"));
const FinancialDashboardPage = lazy(() => import("./routes/staff/dashboard/FinancialDashboardPage.js"));
const CrmPage = lazy(() => import("./routes/staff/crm/CrmPage.js"));
const LeadsTodayPage = lazy(() => import("./routes/staff/leads/LeadsTodayPage.js"));
const LeadsKanbanPage = lazy(() => import("./routes/staff/leads/LeadsKanbanPage.js"));
const LeadsAnalyticsPage = lazy(() => import("./routes/staff/leads/LeadsAnalyticsPage.js"));
const QuotesListPage = lazy(() => import("./routes/staff/quotes/QuotesListPage.js"));
const NewQuotePage = lazy(() => import("./routes/staff/quotes/NewQuotePage.js"));
const EditQuotePage = lazy(() => import("./routes/staff/quotes/EditQuotePage.js"));
const QuoteDetailPage = lazy(() => import("./routes/staff/quotes/QuoteDetailPage.js"));
const SettingsPage = lazy(() => import("./routes/staff/settings/SettingsPage.js"));

export function App() {
  return (
    <BrowserRouter>
      <LeadNotificationsWatcher />
      <Suspense fallback={<div className="p-6 text-sm text-slate-500">Carregando…</div>}>
        <Routes>
          {/* Rotas públicas — sem layout/menu do painel interno */}
          <Route path="/agendar/:linkToken" element={<PublicBookingPage />} />

          {/* Rotas privadas — staff */}
          <Route path="/login" element={<StaffLoginPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <StaffDashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/patients"
            element={
              <RequireAuth>
                <PatientsListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/patients/new"
            element={
              <RequireAuth>
                <NewPatientPage />
              </RequireAuth>
            }
          />
          <Route
            path="/patients/:patientId/photos"
            element={
              <RequireAuth>
                <PatientPhotosPage />
              </RequireAuth>
            }
          />
          <Route
            path="/patients/:patientId/clinical-record"
            element={
              <RequireAuth>
                <ClinicalRecordPage />
              </RequireAuth>
            }
          />
          <Route
            path="/patients/:patientId/edit"
            element={
              <RequireAuth>
                <EditPatientPage />
              </RequireAuth>
            }
          />
          <Route
            path="/agenda"
            element={
              <RequireAuth>
                <AgendaPage />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals"
            element={
              <RequireAuth>
                <ProfessionalsListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/:professionalId/edit"
            element={
              <RequireAuth>
                <EditProfessionalPage />
              </RequireAuth>
            }
          />
          <Route
            path="/services"
            element={
              <RequireAuth>
                <ServicesListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/services/new"
            element={
              <RequireAuth>
                <NewServicePage />
              </RequireAuth>
            }
          />
          <Route
            path="/services/:serviceId/edit"
            element={
              <RequireAuth>
                <NewServicePage />
              </RequireAuth>
            }
          />
          <Route
            path="/service-categories"
            element={
              <RequireAuth>
                <ServiceCategoriesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/invoices"
            element={
              <RequireAuth>
                <InvoicesListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <RequireAuth>
                <NewInvoicePage />
              </RequireAuth>
            }
          />
          <Route
            path="/payables"
            element={
              <RequireAuth>
                <PayablesListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/payables/new"
            element={
              <RequireAuth>
                <NewPayablePage />
              </RequireAuth>
            }
          />
          <Route
            path="/crm"
            element={
              <RequireAuth>
                <CrmPage />
              </RequireAuth>
            }
          />
          <Route
            path="/leads"
            element={
              <RequireAuth>
                <LeadsKanbanPage />
              </RequireAuth>
            }
          />
          <Route
            path="/leads/today"
            element={
              <RequireAuth>
                <LeadsTodayPage />
              </RequireAuth>
            }
          />
          <Route
            path="/leads/analytics"
            element={
              <RequireAuth>
                <LeadsAnalyticsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/financial"
            element={
              <RequireAuth>
                <FinancialDashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/quotes"
            element={
              <RequireAuth>
                <QuotesListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <RequireAuth>
                <NewQuotePage />
              </RequireAuth>
            }
          />
          <Route
            path="/quotes/:quoteId"
            element={
              <RequireAuth>
                <QuoteDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/quotes/:quoteId/edit"
            element={
              <RequireAuth>
                <EditQuotePage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/users"
            element={
              <RequireAuth adminOnly>
                <UsersListPage />
              </RequireAuth>
            }
          />
          <Route
            path="/users/new"
            element={
              <RequireAuth adminOnly>
                <NewUserPage />
              </RequireAuth>
            }
          />
          <Route
            path="/users/:userId/edit"
            element={
              <RequireAuth adminOnly>
                <EditUserPage />
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
